/**
 * ExceptionQueue — the accountant's "what needs a human right now" list.
 *
 * Groups tax jobs into five categories (Payment overdue → Change requested →
 * Deadline in ≤5 days → Missing contact email → Confirmed, awaiting file).
 * A job surfaces in AT MOST one bucket — highest-priority match wins so the
 * queue never double-counts.
 *
 * Every row has an inline action set that delegates to the existing routers:
 *   • provisional_tax + payment_overdue  →  Send slip · Mark paid
 *   • change_requested                    →  Open review · Open messages
 *   • deadline_approaching                →  Nudge (fire next cadence stage)
 *   • no_contact_email                    →  Open client (fix email)
 *   • confirmed_awaiting_file             →  Mark filed · Open job
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCcw, AlertTriangle, Flame, Clock, Mail, MailX, CheckCircle2, PencilLine, ExternalLink, Zap, User, Filter } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const CATEGORY_ICONS = {
    payment_overdue:         Flame,
    change_requested:        PencilLine,
    deadline_approaching:    Clock,
    no_contact_email:        MailX,
    confirmed_awaiting_file: CheckCircle2,
};

const TONES = {
    red:     { chip: "bg-red-100 text-red-800 border-red-200",         header: "text-red-800 border-red-200 bg-red-50" },
    orange:  { chip: "bg-orange-100 text-orange-800 border-orange-200", header: "text-orange-800 border-orange-200 bg-orange-50" },
    amber:   { chip: "bg-amber-100 text-amber-900 border-amber-200",   header: "text-amber-900 border-amber-200 bg-amber-50" },
    slate:   { chip: "bg-slate-100 text-slate-700 border-slate-200",   header: "text-slate-700 border-slate-200 bg-slate-50" },
    emerald: { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", header: "text-emerald-800 border-emerald-200 bg-emerald-50" },
};

const KIND_LABEL = {
    gst_return: "GST return",
    annual_return: "Annual return",
    provisional_tax: "Provisional tax",
};

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
}

function daysOutText(d, category) {
    if (d === null || d === undefined) return "No deadline";
    if (d < 0) return `${Math.abs(d)}d ${category === "payment_overdue" ? "overdue" : "past due"}`;
    if (d === 0) return "Due today";
    if (d === 1) return "Due tomorrow";
    return `${d}d left`;
}

export default function ExceptionQueue() {
    const [categories, setCategories] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [activeCategory, setActiveCategory] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tax/exceptions/");
            setCategories(data.categories || []);
            setSummary(data.summary || {});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function nudge(row) {
        setBusyId(`nudge-${row.id}`);
        try {
            // Ask the tax-cadence router to fire the smallest unfired stage.
            const stages = [2, 5, 7, 10, 14];
            for (const s of stages) {
                try {
                    await api.post(`/tax/${row.id}/fire-cadence`, { stage: s });
                    toast.success(`Sent ${s}-day reminder`);
                    await load();
                    return;
                } catch (e) {
                    // 400 means "stage already fired" — try the next one.
                    if (e?.response?.status !== 400) throw e;
                }
            }
            toast.error("All cadence stages already fired for this job.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    async function sendSlip(row) {
        setBusyId(`slip-${row.id}`);
        try {
            await api.post(`/tax/provisional/${row.id}/send-slip`, {});
            toast.success(`Payment slip emailed to ${row.client_name}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    async function markPaid(row) {
        setBusyId(`paid-${row.id}`);
        try {
            await api.post(`/tax/provisional/${row.id}/mark-paid`, {});
            toast.success("Instalment marked paid");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    async function markFiled(row) {
        setBusyId(`filed-${row.id}`);
        try {
            await api.patch(`/jobs/${row.id}`, { status: "filed" });
            toast.success("Marked filed");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    const filtered = useMemo(() => {
        if (activeCategory === "all") return categories;
        return categories.filter((c) => c.key === activeCategory);
    }, [categories, activeCategory]);

    return (
        <div className="space-y-4">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">Exception queue</h1>
                    <p className="text-sm text-[#5E636E] mt-0.5">
                        Every tax job that needs a human right now — grouped by why. Items disappear the moment the underlying condition is fixed.
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="exq-refresh">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCcw className="h-4 w-4 mr-1" /> Refresh</>}
                </Button>
            </header>

            {/* KPI + category chips (also filter) */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <button
                    type="button"
                    onClick={() => setActiveCategory("all")}
                    className={`text-left rounded-xl border p-3 bg-[#0F1115] text-white ${activeCategory === "all" ? "ring-2 ring-offset-1 ring-[#0F1115]" : ""}`}
                    data-testid="exq-kpi-all"
                >
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] uppercase tracking-wider opacity-80">Everything</span>
                        <AlertTriangle className="h-4 w-4 opacity-70" />
                    </div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums">{summary.total || 0}</div>
                    <div className="text-[11px] opacity-70 mt-0.5">{summary.clients_affected || 0} clients affected</div>
                </button>
                {categories.map((c) => {
                    const Icon = CATEGORY_ICONS[c.key] || AlertTriangle;
                    const active = activeCategory === c.key;
                    const tone = TONES[c.tone]?.header || TONES.slate.header;
                    return (
                        <button
                            key={c.key}
                            type="button"
                            onClick={() => setActiveCategory(active ? "all" : c.key)}
                            className={`text-left rounded-xl border p-3 ${tone} transition ${active ? "ring-2 ring-offset-1 ring-[#0F1115]" : "hover:shadow-sm"}`}
                            data-testid={`exq-kpi-${c.key}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-wider opacity-80">{c.label}</span>
                                <Icon className="h-4 w-4 opacity-70" />
                            </div>
                            <div className="mt-1 text-2xl font-semibold tabular-nums">{c.count}</div>
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <Card className="p-12 text-center text-sm text-[#5E636E]">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading exceptions…
                </Card>
            ) : summary.total === 0 ? (
                <Card className="p-12 text-center" data-testid="exq-empty">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-[#0F1115]">Nothing needs your attention right now.</div>
                    <div className="text-[12px] text-[#5E636E] mt-1">Every tax job is either progressing on schedule or has already been filed.</div>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filtered.map((c) => (
                        c.count === 0 ? null : <CategoryBlock
                            key={c.key}
                            category={c}
                            busyId={busyId}
                            onNudge={nudge}
                            onSlip={sendSlip}
                            onPaid={markPaid}
                            onFiled={markFiled}
                        />
                    ))}
                    {filtered.every((c) => c.count === 0) && (
                        <Card className="p-8 text-center text-sm text-[#5E636E]" data-testid="exq-filter-empty">
                            No items in this category. Tap <b>Everything</b> to see the full queue.
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}

function CategoryBlock({ category, busyId, onNudge, onSlip, onPaid, onFiled }) {
    const Icon = CATEGORY_ICONS[category.key] || AlertTriangle;
    const tone = TONES[category.tone] || TONES.slate;
    return (
        <Card className="p-0 overflow-hidden" data-testid={`exq-cat-${category.key}`}>
            <div className={`flex items-center gap-2 border-b px-3 py-2 ${tone.header}`}>
                <Icon className="h-4 w-4" />
                <div className="text-[13px] font-semibold">{category.label}</div>
                <span className="ml-auto text-[11.5px] tabular-nums">{category.count}</span>
            </div>
            <ul className="divide-y divide-[#EEEDE7]">
                {category.rows.map((r) => (
                    <ExceptionRow
                        key={r.id}
                        row={r}
                        tone={tone}
                        busyId={busyId}
                        onNudge={onNudge}
                        onSlip={onSlip}
                        onPaid={onPaid}
                        onFiled={onFiled}
                    />
                ))}
            </ul>
        </Card>
    );
}

function ExceptionRow({ row, tone, busyId, onNudge, onSlip, onPaid, onFiled }) {
    return (
        <li className="px-3 py-3 flex flex-wrap items-start gap-3" data-testid={`exq-row-${row.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/app/clients/${row.client_id}`} className="text-[14px] font-medium text-[#0F1115] hover:underline truncate" data-testid={`exq-client-${row.id}`}>
                        {row.client_name || "—"}
                    </Link>
                    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10.5px] font-medium ${tone.chip}`}>
                        {daysOutText(row.days_out, row.reason)}
                    </span>
                    <span className="text-[11.5px] text-[#5E636E]">
                        {KIND_LABEL[row.kind] || row.kind}
                        {row.installment_number ? ` · P${row.installment_number}` : ""}
                        {row.period_label ? ` · ${row.period_label}` : ""}
                    </span>
                </div>
                <div className="text-[11.5px] text-[#5E636E] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>Due {fmt(row.filing_deadline)}</span>
                    {row.amount_display ? <span>Amount {row.amount_display}</span> : null}
                    {row.assignee_name ? <span>Assignee {row.assignee_name}</span> : <span className="text-orange-700">Unassigned</span>}
                    {row.cadence_count > 0 && <span>{row.cadence_count} email{row.cadence_count === 1 ? "" : "s"} sent</span>}
                </div>
                {row.reason === "change_requested" && row.latest_change_request && (
                    <div className="mt-2 rounded border border-orange-200 bg-orange-50 p-2 text-[12px] text-[#78511A]" data-testid={`exq-change-${row.id}`}>
                        <div className="font-semibold">
                            {row.latest_change_request.category ? row.latest_change_request.category.replace(/_/g, " ") : "Change"} · {fmt(row.latest_change_request.created_at)}
                        </div>
                        <div className="mt-0.5 line-clamp-3">{row.latest_change_request.explanation}</div>
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-1 ml-auto">
                {row.reason === "payment_overdue" && (
                    <>
                        <Button size="sm" variant="outline" onClick={() => onSlip(row)} disabled={busyId === `slip-${row.id}`} className="h-7 text-[11px]" data-testid={`exq-slip-${row.id}`}>
                            {busyId === `slip-${row.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Mail className="h-3 w-3 mr-1" /> Send slip</>}
                        </Button>
                        <Button size="sm" onClick={() => onPaid(row)} disabled={busyId === `paid-${row.id}`} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`exq-paid-${row.id}`}>
                            {busyId === `paid-${row.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Mark paid</>}
                        </Button>
                    </>
                )}
                {row.reason === "change_requested" && (
                    <>
                        <Link to={`/portal/tax/review/${row.id}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exq-open-review-${row.id}`}>
                                <ExternalLink className="h-3 w-3 mr-1" /> Client view
                            </Button>
                        </Link>
                        <Link to={`/app/inbox?client=${row.client_id}`}>
                            <Button size="sm" className="h-7 text-[11px] bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid={`exq-msg-${row.id}`}>
                                Message client
                            </Button>
                        </Link>
                    </>
                )}
                {row.reason === "deadline_approaching" && (
                    <Button size="sm" variant="outline" onClick={() => onNudge(row)} disabled={busyId === `nudge-${row.id}` || row.kind === "gst_return" || row.kind === "annual_return" || row.kind === "provisional_tax" ? false : true} className="h-7 text-[11px]" data-testid={`exq-nudge-${row.id}`}>
                        {busyId === `nudge-${row.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Zap className="h-3 w-3 mr-1" /> Nudge</>}
                    </Button>
                )}
                {row.reason === "no_contact_email" && (
                    <Link to={`/app/clients/${row.client_id}`}>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exq-fix-email-${row.id}`}>
                            <User className="h-3 w-3 mr-1" /> Add email
                        </Button>
                    </Link>
                )}
                {row.reason === "confirmed_awaiting_file" && (
                    <>
                        <Button size="sm" onClick={() => onFiled(row)} disabled={busyId === `filed-${row.id}`} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`exq-file-${row.id}`}>
                            {busyId === `filed-${row.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Mark filed</>}
                        </Button>
                        <Link to={`/app/jobs?q=${encodeURIComponent(row.client_name || "")}`}>
                            <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`exq-open-job-${row.id}`}>
                                <ExternalLink className="h-3 w-3 mr-1" /> Open job
                            </Button>
                        </Link>
                    </>
                )}
            </div>
        </li>
    );
}
